/**
 * The selected month's CARs, family -> category -> CAR.
 *
 * Three levels, both upper ones collapsed by default: the point of the table is
 * that a reader sees five numbers, opens the one that moved, and reaches the
 * individual CARs behind it without leaving the page.
 *
 * Counts come from the server (`family_summary`, `category_summary`), not from
 * counting the rows rendered underneath. Those are the same numbers computed
 * from the same frame, and deriving them here would be a second implementation
 * that disagrees the first time a CAR is filtered out of one level but not the
 * other.
 */

import { Fragment, useState } from 'react'

import { Card, Empty } from '../shared/Card'
import { displayText, formatDate, monthLabel, summarize } from '../shared/format'
import { count, type Keyed, text } from '../shared/rows'

import { COLOR_BY_FAMILY } from './families'

interface Props {
  familyRows: Keyed[]
  categoryRows: Keyed[]
  detailRows: Keyed[]
  selectedMonth: string | null
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span className={`chevron${open ? ' is-open' : ''}`} aria-hidden="true">
      <svg width="10" height="10" viewBox="0 0 10 10">
        <path d="M3 1 L7 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    </span>
  )
}

export function CategoryBreakdown({
  familyRows,
  categoryRows,
  detailRows,
  selectedMonth,
}: Props) {
  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set())
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set())

  const toggle = (set: Set<string>, key: string, apply: (next: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    apply(next)
  }

  const total = familyRows.reduce((sum, row) => sum + count(row.car_count), 0)
  const subtitle =
    selectedMonth === null
      ? 'No selected month'
      : `${monthLabel(selectedMonth)} — ${total.toLocaleString()} ${total === 1 ? 'CAR' : 'CARs'}. Open a family to see its categories, and a category to see the CARs.`

  if (selectedMonth === null || familyRows.length === 0) {
    return (
      <Card title="Complaint categories" subtitle={subtitle}>
        <Empty label="No CARs for this month." />
      </Card>
    )
  }

  return (
    <Card title="Complaint categories" subtitle={subtitle}>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table category-tree">
          <thead>
            <tr>
              <th className="chevron-cell" aria-label="Expand" />
              <th>Category</th>
              <th className="cell-number">CARs</th>
              <th>Vendor</th>
              <th>Item</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {familyRows.map((family) => {
              const familyName = displayText(text(family.family))
              const familyOpen = openFamilies.has(familyName)
              const categories = categoryRows.filter(
                (row) => displayText(text(row.family)) === familyName,
              )
              return (
                <Fragment key={familyName}>
                  <tr
                    className={`detail-row tree-family${familyOpen ? ' is-open' : ''}`}
                    onClick={() => {
                      toggle(openFamilies, familyName, setOpenFamilies)
                    }}
                  >
                    <td className="chevron-cell">
                      <Chevron open={familyOpen} />
                    </td>
                    <td>
                      <span
                        className="stack-swatch"
                        style={{ background: COLOR_BY_FAMILY.get(familyName) ?? 'var(--text-muted)' }}
                      />
                      <strong>{familyName}</strong>
                    </td>
                    <td className="cell-number">{count(family.car_count).toLocaleString()}</td>
                    <td colSpan={3} />
                  </tr>

                  {familyOpen &&
                    categories.map((category) => {
                      const categoryKey = displayText(text(category.primary_category))
                      const categoryName = displayText(text(category.category_label))
                      const categoryOpen = openCategories.has(categoryKey)
                      const cars = detailRows.filter(
                        (row) => displayText(text(row.primary_category)) === categoryKey,
                      )
                      return (
                        <Fragment key={categoryKey || categoryName}>
                          <tr
                            className={`detail-row tree-category${categoryOpen ? ' is-open' : ''}`}
                            onClick={() => {
                              toggle(openCategories, categoryKey, setOpenCategories)
                            }}
                          >
                            <td className="chevron-cell" />
                            <td className="tree-indent-1">
                              <Chevron open={categoryOpen} />
                              {categoryName}
                            </td>
                            <td className="cell-number">
                              {count(category.car_count).toLocaleString()}
                            </td>
                            <td colSpan={3} />
                          </tr>

                          {categoryOpen &&
                            cars.map((car) => (
                              <tr key={displayText(text(car.car_id))} className="tree-car">
                                <td className="chevron-cell" />
                                <td className="tree-indent-2 cell-mono">
                                  {displayText(text(car.car_number))}
                                  <span className="tree-date">
                                    {formatDate(text(car.request_date))}
                                  </span>
                                </td>
                                <td />
                                <td
                                  className="cell-truncate"
                                  title={displayText(text(car.supplier_name))}
                                >
                                  {displayText(text(car.supplier_name))}
                                </td>
                                <td className="cell-truncate" title={displayText(text(car.item_name))}>
                                  {displayText(text(car.item_name))}
                                </td>
                                <td
                                  className="cell-summary"
                                  title={displayText(text(car.complaint_text))}
                                >
                                  {/* The model's one-line summary where there is
                                      one, the complaint text where there is not.
                                      Never a blank cell: an uncategorised CAR
                                      still has a complaint worth reading. */}
                                  {displayText(text(car.category_summary)) ||
                                    summarize(text(car.complaint_text))}
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      )
                    })}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
